import { useSeo } from "../hooks/useSeo";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronLeft, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatHuf } from "../context/CartContext";
import { useRevealAll } from "../hooks/useReveal";

// Relative URL — calls same origin the page was served from (preview, production, custom domain all work).
const API_BASE = "";

const isUsablePriceId = (pid) =>
  typeof pid === "string" && pid.trim().length > 0;

export default function Checkout() {
  useSeo({
    title: "Pénztár | Hair Elixir Hungary",
    description: "",
    path: "/penztar",
    noindex: true,
  });
  const { items, subtotal, removeItem } = useCart();
  const ref = useRef(null);
  useRevealAll(ref);
  const [submitting, setSubmitting] = useState(false);

  // Partition cart into usable items and stale (ghost-cart) items.
  // Stale = stripe_price_id missing / null / empty — produced by old cart entries
  // saved before Stripe price IDs were available, or for archived products.
  const { validItems, staleItems } = useMemo(() => {
    const valid = [];
    const stale = [];
    for (const it of items) {
      if (isUsablePriceId(it.stripe_price_id)) valid.push(it);
      else stale.push(it);
    }
    return { validItems: valid, staleItems: stale };
  }, [items]);

  const validSubtotal = useMemo(
    () => validItems.reduce((sum, it) => sum + (it.unitPrice || 0) * (it.qty || 0), 0),
    [validItems],
  );

  // One-shot warning toast when the page first detects stale items.
  const [warned, setWarned] = useState(false);
  useEffect(() => {
    if (staleItems.length > 0 && !warned) {
      toast.warning("A kosár egy tételét frissíteni kell", {
        description:
          "Kérlek távolítsd el és add hozzá újra a kívánt terméket.",
      });
      setWarned(true);
    }
  }, [staleItems.length, warned]);

  const startCheckout = async () => {
    if (validItems.length === 0 || submitting) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: validItems.map((it) => ({
            stripe_price_id: it.stripe_price_id,
            quantity: it.qty,
          })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error("Nem sikerült a fizetés indítása", {
          description: err.detail || "Próbáld újra később.",
        });
        setSubmitting(false);
        return;
      }

      const data = await res.json();
      if (!data.url) {
        toast.error("Hiba történt", { description: "A fizetési munkamenet URL-je hiányzik." });
        setSubmitting(false);
        return;
      }

      window.location.href = data.url;
    } catch (e) {
      toast.error("Hálózati hiba", { description: "Ellenőrizd az internetkapcsolatot és próbáld újra." });
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-40 pb-32 he-container min-h-[60vh] text-center">
        <p className="overline mb-5">Pénztár</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-6">
          A kosár <span className="italic text-[color:var(--he-gold)]">üres.</span>
        </h1>
        <p className="text-[color:var(--he-ink-soft)] font-light max-w-md mx-auto leading-relaxed">
          Még nincs termék a kosaradban. Nézd át az aktuális kínálatot.
        </p>
        <Link to="/termekek" className="mt-10 inline-flex btn-outline-gold" data-testid="empty-checkout-cta">
          <span>Termékek megtekintése</span>
          <ArrowRight size={14} />
        </Link>
      </main>
    );
  }

  return (
    <main ref={ref} data-testid="page-checkout" className="pt-28 md:pt-32 bg-[color:var(--he-bg)] min-h-screen">
      <div className="he-container py-8">
        <Link to="/termekek" className="btn-ghost" data-testid="back-from-checkout">
          <ChevronLeft size={14} className="ghost-arrow" />
          <span>Tovább vásárlás</span>
        </Link>
      </div>

      <section className="he-container pb-32">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="md:col-span-7 reveal">
            <p className="overline mb-5 mag-marker">
              <span>Pénztár</span>
            </p>
            <h1
              className="font-serif-display tracking-[-0.022em] leading-[1.04]"
              style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)", fontWeight: 400 }}
            >
              Egy kattintás a <span className="italic text-[color:var(--he-gold)]">biztonságos fizetésig.</span>
            </h1>
            <p className="mt-6 text-[color:var(--he-ink-soft)] font-light max-w-xl leading-[1.7]">
              A szállítási címet és a kártyaadatokat a Stripe biztonságos felületén
              kéri be a rendszer. Magyarországra szállítunk, az adataid végig
              titkosított csatornán haladnak.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9 reveal delay-1 flex md:items-end md:justify-end">
            <div className="inline-flex items-center gap-3 text-[color:var(--he-ink-soft)] text-sm">
              <Lock size={14} className="text-[color:var(--he-gold)]" />
              <span>Stripe · titkosított</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Order details */}
          <div className="lg:col-span-7 space-y-10">
            <div className="reveal">
              <div className="flex items-center gap-4 mb-7">
                <span className="figure italic text-[color:var(--he-gold)] text-2xl">01</span>
                <h2 className="font-serif-display text-2xl md:text-3xl">Rendelt tételek</h2>
                <span className="flex-1 h-px bg-[color:var(--he-line)]" />
              </div>
              <ul className="divide-y divide-[color:var(--he-line)]">
                {items.map((it) => {
                  const isStale = !isUsablePriceId(it.stripe_price_id);
                  return (
                    <li
                      key={it.key}
                      className={`flex gap-5 py-5 ${isStale ? "opacity-60" : ""}`}
                      data-testid={`ck-item-${it.key}`}
                    >
                      <div className="w-20 h-24 flex-shrink-0 overflow-hidden bg-[color:var(--he-cream)]">
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="overline-xs text-[color:var(--he-ink-soft)]">
                          {it.length} · {it.weight} · {it.qty} db
                        </p>
                        <h5 className="font-serif-display text-lg leading-tight mt-1">{it.name}</h5>
                        <p className="text-sm font-light text-[color:var(--he-ink-soft)] mt-1">
                          {formatHuf(it.unitPrice)} / db
                        </p>
                        {isStale && (
                          <button
                            type="button"
                            onClick={() => removeItem(it.key)}
                            data-testid={`ck-remove-stale-${it.key}`}
                            className="mt-2 inline-flex items-center gap-2 text-xs text-[color:var(--he-gold)] underline-offset-2 hover:underline"
                          >
                            <AlertTriangle size={12} />
                            <span>Frissítés szükséges — kattints az eltávolításhoz, majd add hozzá újra</span>
                          </button>
                        )}
                      </div>
                      <div className="text-right font-serif-display text-base whitespace-nowrap">
                        {formatHuf(it.unitPrice * it.qty)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {staleItems.length > 0 && (
              <div
                role="alert"
                data-testid="ck-stale-banner"
                className="reveal delay-1 border border-[color:var(--he-gold)] bg-[color:var(--he-cream)] p-5 text-sm font-light leading-relaxed flex gap-3"
              >
                <AlertTriangle size={18} className="text-[color:var(--he-gold)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-serif-display text-base mb-1">A kosár egy tételét frissíteni kell.</p>
                  <p className="text-[color:var(--he-ink-soft)]">
                    Kérlek távolítsd el és add hozzá újra a kívánt terméket. A jelölt tételeket nem
                    számoljuk be a végösszegbe és a fizetésbe.
                  </p>
                </div>
              </div>
            )}

            <div className="reveal delay-1 text-sm font-light text-[color:var(--he-ink-soft)] leading-relaxed">
              <p>
                A következő lépésben a Stripe biztonságos fizetési felülete kéri be a szállítási
                címet, telefonszámot és a kártyaadatokat. Csak Magyarországra szállítunk.
              </p>
            </div>
          </div>

          {/* Order summary */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 bg-[color:var(--he-cream)] border border-[color:var(--he-line)] p-7 md:p-9">
              <p className="overline mb-6">Rendelés összesítő</p>
              <div className="space-y-2 pb-5 border-b border-[color:var(--he-line)] text-sm font-light">
                <Row label="Részösszeg" value={formatHuf(validSubtotal)} />
                <Row label="Szállítás" value="A Stripe-on választható" />
              </div>
              <div className="flex items-baseline justify-between pt-5 mb-7">
                <p className="overline">Termékek összesen</p>
                <p className="font-serif-display text-3xl" data-testid="ck-total">
                  {formatHuf(validSubtotal)}
                </p>
              </div>
              <button
                type="button"
                onClick={startCheckout}
                data-testid="ck-submit"
                disabled={submitting || validItems.length === 0}
                className="btn-ink w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>
                  {submitting
                    ? "Átirányítás…"
                    : validItems.length === 0
                    ? "Frissítsd a kosarat"
                    : "Tovább a fizetéshez"}
                </span>
                {!submitting && validItems.length > 0 && <ArrowRight size={14} />}
              </button>
              <p className="text-xs text-[color:var(--he-ink-soft)] mt-5 text-center font-light">
                A folytatással átirányítunk a Stripe biztonságos fizetési oldalára.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[color:var(--he-ink-soft)]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
